import React, { useState, useEffect } from 'react';
import PortfolioList from './PortfolioList';
import PortfolioModal from './PortfolioModal';
import NewsList from './NewsList';
import NewsModal from './NewsModal';
import ContactSubmissionsList from './ContactSubmissionsList';
import ConsultationRequestsList from './ConsultationRequestsList';
import EAAScanRequestsList from './EAAScanRequestsList';
import { FileText, Briefcase, Mail, Calendar, Shield } from 'lucide-react';

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('portfolio');
  const [portfolios, setPortfolios] = useState([]);
  const [news, setNews] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [scans, setScans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadPortfolios();
    loadNews();
    loadContacts();
    loadConsultations();
    loadScans();
  }, [refreshTrigger]);

  const loadPortfolios = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await fetch('/api/portfolio.php?admin=true&limit=100');
      const result = await response.json();
      
      if (result.success) {
        setPortfolios(result.data || []);
      } else {
        setError('Failed to load portfolios: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error loading portfolios:', err);
      setError('Failed to connect to API. Please check your server configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadNews = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await fetch('/api/news.php?admin=true&limit=100');
      const result = await response.json();
      
      if (result.success) {
        setNews(result.data || []);
      } else {
        setError('Failed to load news: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error loading news:', err);
      setError('Failed to connect to API. Please check your server configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await fetch('/api/contact.php?action=get_all');
      const result = await response.json();
      
      if (result.success) {
        setContacts(result.data || []);
      } else {
        setError('Failed to load contacts: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error loading contacts:', err);
      setError('Failed to connect to API. Please check your server configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadConsultations = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await fetch('/api/consultation.php?action=get_all');
      const result = await response.json();
      
      if (result.success) {
        setConsultations(result.data || []);
      } else {
        setError('Failed to load consultations: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error loading consultations:', err);
      setError('Failed to connect to API. Please check your server configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadScans = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await fetch('/api/eaa-scanner.php?action=get_all');
      const result = await response.json();
      
      if (result.success) {
        setScans(result.data || []);
      } else {
        setError('Failed to load scans: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error loading scans:', err);
      setError('Failed to connect to API. Please check your server configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDeletePortfolio = async (portfolioId) => {
    try {
      const response = await fetch(`/api/portfolio.php?id=${portfolioId}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (result.success) {
        setPortfolios(prev => prev.filter(p => p.id !== portfolioId));
      } else {
        alert('Error deleting portfolio: ' + result.error);
      }
    } catch (err) {
      console.error('Error deleting portfolio:', err);
      alert('Failed to delete portfolio item');
    }
  };

  const handleDeleteNews = async (newsId) => {
    try {
      const response = await fetch(`/api/news.php?id=${newsId}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (result.success) {
        setNews(prev => prev.filter(n => n.id !== newsId));
      } else {
        alert('Error deleting article: ' + result.error);
      }
    } catch (err) {
      console.error('Error deleting article:', err);
      alert('Failed to delete article');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleSaveSuccess = () => {
    setShowModal(false);
    setEditingItem(null);
    setRefreshTrigger(prev => prev + 1);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  const tabs = [
    { id: 'portfolio', name: 'Portfolio', icon: Briefcase, count: portfolios.length },
    { id: 'news', name: 'News & Articles', icon: FileText, count: news.length },
    { id: 'contacts', name: 'Contact Forms', icon: Mail, count: contacts.length },
    { id: 'consultations', name: 'Consultations', icon: Calendar, count: consultations.length },
    { id: 'scans', name: 'EAA Scans', icon: Shield, count: scans.length }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">KGC Innovations Admin</h1>
              <p className="text-sm text-gray-600">Content Management Dashboard</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">Welcome, Admin</span>
              <button
                onClick={onLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-8">
          <div className="sm:hidden">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tab.name} ({tab.count})
                </option>
              ))}
            </select>
          </div>
          <div className="hidden sm:block">
            <nav className="flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
                  >
                    <Icon className="w-5 h-5 mr-2" />
                    {tab.name}
                    <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2.5 rounded-full text-xs font-medium">
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {activeTab === 'portfolio' && 'Portfolio Items'}
                {activeTab === 'news' && 'News & Articles'}
                {activeTab === 'contacts' && 'Contact Submissions'}
                {activeTab === 'consultations' && 'Consultation Requests'}
                {activeTab === 'scans' && 'EAA Scan Requests'}
              </h2>
              <p className="text-gray-600">
                {activeTab === 'portfolio' && 'Manage your portfolio projects and case studies'}
                {activeTab === 'news' && 'Manage your news articles and blog posts'}
                {activeTab === 'contacts' && 'Manage contact form submissions and inquiries'}
                {activeTab === 'consultations' && 'Manage consultation bookings and schedule appointments'}
                {activeTab === 'scans' && 'Manage accessibility scan requests and results'}
              </p>
            </div>
            {(activeTab === 'portfolio' || activeTab === 'news') && (
              <button
                onClick={handleAddNew}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium inline-flex items-center transition-colors duration-200"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {activeTab === 'portfolio' ? 'Add New Portfolio' : 'Add New Article'}
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Content Lists */}
        <div className="bg-white shadow rounded-lg">
          {activeTab === 'portfolio' && (
            <PortfolioList
              portfolios={portfolios}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDeletePortfolio}
              formatDate={formatDate}
            />
          )}
          {activeTab === 'news' && (
            <NewsList
              news={news}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDeleteNews}
              formatDate={formatDate}
            />
          )}
          {activeTab === 'contacts' && (
            <div className="p-6">
              <ContactSubmissionsList />
            </div>
          )}
          {activeTab === 'consultations' && (
            <div className="p-6">
              <ConsultationRequestsList />
            </div>
          )}
          {activeTab === 'scans' && (
            <div className="p-6">
              <EAAScanRequestsList />
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      {showModal && activeTab === 'portfolio' && (
        <PortfolioModal
          portfolio={editingItem}
          onClose={handleModalClose}
          onSaveSuccess={handleSaveSuccess}
        />
      )}
      
      {showModal && activeTab === 'news' && (
        <NewsModal
          article={editingItem}
          onClose={handleModalClose}
          onSaveSuccess={handleSaveSuccess}
        />
      )}
    </div>
  );
};

export default AdminDashboard;